import { useEffect, useState, useCallback } from "react";
import * as XLSX from "xlsx";

import { useGetProductsQuery } from "../../slices/productsApiSlice";
import { useParams } from "react-router-dom";
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";
import { useGetTransfersQuery } from "../../slices/transfersApiSlice";
import { useGetStocksQuery } from "../../slices/stocksApiSlice";
import { useGetOpeningStocksQuery } from "../../slices/openingStocksApiSlice";

export default function UseStockAnalysis() {
  const { pageNumber } = useParams();

  // Fetch data using RTK Query
  const { data: productData, isLoading: productsLoading, error: productsError } = useGetProductsQuery({ pageNumber });
  const { data: salesData } = useGetSalesQuery({ pageNumber });
  const { data: suppliesData } = useGetSuppliesQuery({ pageNumber });
  const { data: stocksData } = useGetStocksQuery({ pageNumber });
  const { data: transfersData } = useGetTransfersQuery({ pageNumber });
  const { data: openingStockData } = useGetOpeningStocksQuery();
  const [searchQuery, setSearchQuery] = useState("");

  // State for holding data
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [stocks, setStocks] = useState([]);

  const [firstDate, setFirstDate] = useState("");
  const [secondDate, setSecondDate] = useState(new Date().toISOString().split("T")[0]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (productsLoading) return;

    if (productsError || !productData) {
      console.error("Error fetching product data:", productsError);
      return;
    }
    setProducts(productData.products || []);
    setSales(salesData?.sales || []);
    setSupplies(suppliesData?.supplies || []);
    setTransfers(transfersData?.transfers || []);
    setStocks(stocksData?.stocks || []);

    if (stocksData?.stocks.length > 0) {
      const earliestStockDate = stocksData.stocks
        .map((item) => new Date(item.date))
        .reduce((minDate, currentDate) => (currentDate < minDate ? currentDate : minDate), new Date());
      setFirstDate(earliestStockDate.toISOString().split("T")[0]);
    }
  }, [productData, salesData, suppliesData, stocksData, transfersData, productsError, productsLoading]);

  // Helper Functions
  const getInitialStock = (product) => {
    return stocks
      .flatMap((stock) =>
        stock.stockItems?.filter(
          (item) => item?.name === product.name && new Date(stock.date) <= new Date(firstDate)
        ) || []
      )
      .reduce((total, item) => total + (item.qty || 0), 0);
  };

  const calculateMetrics = (product, type) => {
    const dataMap = {
      sales: sales,
      supplies: supplies,
      transfers: transfers,
    };

    const itemKeyMap = {
      sales: "saleItems",
      supplies: "supplyItems",
      transfers: "transferItems",
    };

    return (
      dataMap[type]
        ?.flatMap((entry) =>
          entry[itemKeyMap[type]]
            ?.filter(
              (item) =>
                item?.name === product.name &&
                new Date(entry.date) >= new Date(firstDate) &&
                new Date(entry.date) <= new Date(secondDate)
            ) || []
        )
        .reduce((total, item) => total + (item.qty || 0), 0) || 0
    );
  };

  const getCountedStock = (product) => {
    return stocks
      .flatMap((stock) =>
        stock.stockItems?.filter(
          (item) =>
            item?.name === product.name &&
            new Date(stock.date).toISOString().split("T")[0] === secondDate
        ) || []
      )
      .reduce((total, item) => total + (item.qty || 0), 0);
  };

  const calculateAverageCost = (productName) => {
    const openingStockItems = openingStockData?.openingStocks
      ?.flatMap((openingStock) => openingStock.openingStockItems)
      ?.filter((item) => item.name === productName);

    const qty = openingStockItems?.reduce((total, item) => total + item.qty, 0) || 0;
    const itemCost = openingStockItems?.reduce((total, item) => total + item.itemCost, 0) || 0;

    const Pqty = calculatePqty(productName);
    const Pcost = calculatePcost(productName);

    const totalQty = qty + Pqty;
    const totalCost = itemCost + Pcost;

    return totalQty > 0 ? totalCost / totalQty : 0;
  };

  const calculatePqty = (productName) => {
    return suppliesData?.supplies
      ?.flatMap((supply) => supply.supplyItems)
      ?.filter((item) => item.name === productName)
      ?.reduce((total, item) => total + item.qty, 0) || 0;
  };

  const calculatePcost = (productName) => {
    return suppliesData?.supplies
      ?.flatMap((supply) => supply.supplyItems)
      ?.filter((item) => item.name === productName)
      ?.reduce((total, item) => total + item.itemCost, 0) || 0;
  };
   const filteredBySearch = filteredProducts.filter(product =>
  product.name.toLowerCase().includes(searchQuery.toLowerCase())
   );

  const calculateReport = useCallback(() => {
  const updatedProducts = products.map((product) => {
    if (!product || !product.name) return null;

    const averageCost = calculateAverageCost(product.name);
    const price = product.price;
    const initialStock = getInitialStock(product);
    const purchases = calculateMetrics(product, "supplies");
    const salesQty = calculateMetrics(product, "sales");
    const transfersQty = calculateMetrics(product, "transfers");
    const countedStock = getCountedStock(product);
    const closingStock = initialStock + purchases - salesQty - transfersQty;

    const totalCost = averageCost * closingStock;
    const profit = salesQty * product.price - salesQty * averageCost;
    const difference = countedStock - closingStock;
    const stockValue = closingStock * product.price || 0;
    const balanceValue = difference * product.price || 0;

    return {
      ...product,
      price,
      initialStock,
      purchases,
      salesQty,
      transfersQty,
      closingStock,
      countedStock,
      difference,
      stockValue: stockValue.toFixed(2),
      balanceValue: balanceValue.toFixed(2),
      averageCost: averageCost.toFixed(2),
      totalCost: totalCost.toFixed(2),
      profit: profit.toFixed(2),
    };
  }).filter(Boolean); // Remove null values

  // Apply filters
  let filtered = updatedProducts.filter((product) => {
    switch (filter) {
      case "positive":
        return product.closingStock > 0;
      case "negative":
        return product.closingStock < 0;
      case "topPositive":
        return product.closingStock > 0;
      case "topNegative":
        return product.closingStock < 0;
      case "lowBalance":
        return product.closingStock < 10;
      default:
        return true;
    }
  });

  // Sort for top positive/negative
  if (filter === "topPositive") {
    filtered = filtered.sort((a, b) => b.closingStock - a.closingStock).slice(0, 10);
  } else if (filter === "topNegative") {
    filtered = filtered.sort((a, b) => a.closingStock - b.closingStock).slice(0, 10);
  }

  // **Define and Apply Search Filtering**
  const filteredBySearch = searchQuery
    ? filtered.filter((product) =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filtered;

  setFilteredProducts(filteredBySearch);
}, [filter, products, searchQuery]); // Added searchQuery as a dependency

useEffect(() => {
  calculateReport();
}, [calculateReport]); // Only depends on calculateReport


  const getGrandTotal = (field) => {
    return filteredProducts.reduce((total, product) => total + Number(product[field] || 0), 0).toFixed(2);
  };

  const exportToExcel = () => {
    const dataToExport = filteredProducts.map((p) => ({
      ID: p.id,
      Price: p.price,
      Name: p.name,
      InitialStock: p.initialStock,
      Purchases: p.purchases,
      Sales: p.salesQty,
      Transfers: p.transfersQty,
      ClosingStock: p.closingStock,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Analysis");
    XLSX.writeFile(workbook, "StockAnalysis.xlsx");
  };

  return (
    <div className="stock-analysis">
      <h1>Stock Analysis</h1>
      <div className="search-box">
    <input
    type="text"
    placeholder="Search products..."
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
     />
    </div>
  
      {/* Date Range Input */}
      <div className="date-range">
        <label>
          Start Date:
          <input
            type="date"
            value={firstDate}
            onChange={(e) => setFirstDate(e.target.value)}
          />
        </label>
        <label>
          End Date:
          <input
            type="date"
            value={secondDate}
            onChange={(e) => setSecondDate(e.target.value)}
          />
        </label>
      </div>
  
      {/* Actions */}
      <div className="filters">
        <button onClick={() => calculateReport()}>Generate Full Report</button>
        <button onClick={exportToExcel}>Export to Excel</button>
      </div>
  
      {/* Summary Section */}
      <div style={{display: "none"}} className="summary">
        <h2>Total Stock Value: ${getGrandTotal("stockValue")}</h2>
        <h2>Total Balance Value: ${getGrandTotal("balanceValue")}</h2>
        <h2>Total Cost: ${getGrandTotal("totalCost")}</h2>
        <h2>Total Profit: ${getGrandTotal("profit")}</h2>
      </div>

      {/* Filter Buttons */}
      <div className="mb-3">
        {[
          { label: "Show All Balances", value: "all", class: "btn-outline-secondary" },
          { label: "Show Positive Balances", value: "positive", class: "btn-outline-info" },
          { label: "Show Negative Balances", value: "negative", class: "btn-outline-warning" },
          { label: "Show Low Balance Items", value: "lowBalance", class: "btn-outline-warning" },
        ].map((btn) => (
          <button key={btn.value} type="button" className={`btn ${btn.class}`} onClick={() => setFilter(btn.value)}>
            {btn.label}
          </button>
        ))}
      </div>
  
      {/* Data Table */}
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Price</th>
            <th>Initial Stock</th>
            <th>Purchases</th>
            <th>Sales</th>
            <th>Transfers</th>
            <th>Closing Stock</th>
            
            
          </tr>
        </thead>
        <tbody>
         {filteredBySearch.map((product) => (
        <tr key={product?.id || Math.random()}>
      <td>{product?.name || "N/A"}</td>
      <td>{product?.price || "N/A"}</td>
      <td>{product?.initialStock || 0}</td>
      <td>{product?.purchases || 0}</td>
      <td>{product?.salesQty || 0}</td>
      <td>{product?.transfersQty || 0}</td>
      <td>{product?.closingStock || 0}</td>
      </tr>
      ))}
      </tbody>
      </table>
    </div>
  );
}